import 'client-only';

import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { DATA_KEYS } from '@/consts/dataKeys';
import { claimPromotion } from '@/services/promotionService/actions';
import { type PromotionClaim } from '@/types/productSelectionAndCheckout/types';

import { useProductSelectionAndCheckoutCommonData } from './useProductSelectionAndCheckoutCommonData';

const promotionClaimNotSet = Symbol('promotionClaimNotSet');
const promotionClaimAtom = atom<
  PromotionClaim | typeof promotionClaimNotSet | undefined
>(promotionClaimNotSet);

let hasExecutedClaimPromotion = false;

export const usePromotionClaim = () => {
  const { brand, treatmentCategory } = useFlowContext();
  const { initialPromotionClaim } = useProductSelectionAndCheckoutCommonData();
  const promotion = useDataProvider(DATA_KEYS.promotion);

  const [promotionClaim, setPromotionClaim] = useAtom(promotionClaimAtom);

  const currentPromotionClaim =
    promotionClaim === promotionClaimNotSet
      ? initialPromotionClaim
      : promotionClaim;

  useEffect(() => {
    if (
      // Don't try to claim a promotion if there is no promotion code
      promotion?.code === undefined ||
      // Don't try to claim a promotion if the promotion code is already claimed
      currentPromotionClaim?.code === promotion?.code ||
      // Only claim the promotion once (prevent multiple uses of the hook from spawning multiple claim requests)
      hasExecutedClaimPromotion
    ) {
      return;
    }

    hasExecutedClaimPromotion = true;
    claimPromotion(promotion?.code, brand, treatmentCategory).then(
      (promotionClaim) => {
        if (!promotionClaim) {
          return;
        }
        setPromotionClaim({
          amount: promotionClaim.amountInDollars * 100,
          code: promotionClaim.promotionCode,
          promotionCodeId: promotionClaim.promotionId,
        });
      },
    );
  }, [
    brand,
    currentPromotionClaim?.code,
    promotion?.code,
    setPromotionClaim,
    treatmentCategory,
  ]);

  return { promotionClaim: currentPromotionClaim, setPromotionClaim };
};
